import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';
import { useTheme } from '../context/ThemeContext';
import { ThemedText, Button } from '../components';
import authService from '../api/services/authService';

const QrContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
`;

const QrCodeWrapper = styled.div`
  padding: ${props => props.theme.spacing.lg};
  background-color: #ffffff;
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatusBadge = styled.div`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  background-color: ${props => {
    switch (props.$status) {
      case 'completed': return props.theme.colors.success + '20';
      case 'expired': return props.theme.colors.danger + '20';
      default: return props.theme.colors.info + '20';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'completed': return props.theme.colors.success;
      case 'expired': return props.theme.colors.danger;
      default: return props.theme.colors.info;
    }
  }};
`;

const TimerText = styled.div`
  font-size: 13px;
  color: ${props => props.theme.colors.muted};
`;

const QrLoginDisplay = () => {
  const theme = useTheme();
  const [state, setState] = useState('idle'); // idle | generating | active | completed | expired | error
  const [sessionId, setSessionId] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const pollIntervalRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const mountedRef = useRef(true);

  const cleanup = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  const startPolling = useCallback((sid) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = setInterval(async () => {
      if (!mountedRef.current) return;
      try {
        const result = await authService.getQrLoginSessionStatus(sid);
        if (!mountedRef.current) return;
        if (result.status === 'completed') {
          setState('completed');
          cleanup();
        } else if (result.status === 'expired') {
          setState('expired');
          cleanup();
        }
      } catch {
        // Silently ignore polling errors
      }
    }, 3000);
  }, [cleanup]);

  const startTimer = useCallback((expiryDate) => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    const updateTimer = () => {
      if (!mountedRef.current) return;
      const now = new Date();
      const remaining = Math.max(0, Math.floor((expiryDate.getTime() - now.getTime()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) {
        setState('expired');
        cleanup();
      }
    };
    updateTimer();
    timerIntervalRef.current = setInterval(updateTimer, 1000);
  }, [cleanup]);

  const handleGenerate = async () => {
    cleanup();
    setSessionId(null);
    setState('generating');
    setErrorMessage('');

    try {
      const result = await authService.createQrLoginSession();
      if (!mountedRef.current) return;
      if (!result || !result.sessionId) {
        setState('error');
        setErrorMessage('Invalid response from server. Please try again.');
        return;
      }
      const expiry = new Date(result.expiresAt);
      if (isNaN(expiry.getTime())) {
        setState('error');
        setErrorMessage('Invalid session expiry. Please try again.');
        return;
      }
      setSessionId(result.sessionId);
      setExpiresAt(expiry);
      setState('active');
      startPolling(result.sessionId);
      startTimer(expiry);
    } catch (error) {
      if (!mountedRef.current) return;
      console.error('QR session creation failed:', error);
      setState('error');
      setErrorMessage(error.message || 'Failed to generate QR code. Please try again.');
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const qrData = sessionId
    ? JSON.stringify({ sessionId, type: 'aqrate-mobile-login' })
    : '';

  return (
    <QrContainer theme={theme}>
      {state === 'idle' && (
        <>
          <ThemedText style={{ color: theme.colors.muted, textAlign: 'center' }}>
            Generate a QR code to quickly log in on your mobile device.
            Open the aQRate mobile app and scan the code.
          </ThemedText>
          <Button variant="primary" onClick={handleGenerate}>
            Generate QR Code for Mobile Login
          </Button>
        </>
      )}

      {state === 'generating' && (
        <ThemedText style={{ color: theme.colors.muted }}>
          Generating QR code...
        </ThemedText>
      )}

      {state === 'active' && (
        <>
          <QrCodeWrapper theme={theme}>
            <QRCodeSVG
              value={qrData}
              size={200}
              level="M"
              includeMargin={false}
            />
          </QrCodeWrapper>
          <StatusBadge theme={theme} $status="pending">
            Waiting for scan...
          </StatusBadge>
          <TimerText theme={theme}>
            Expires in {formatTime(timeLeft)}
          </TimerText>
          <ThemedText type="small" style={{ color: theme.colors.muted, textAlign: 'center' }}>
            Open the aQRate mobile app, tap "Scan QR Code to Login" and scan this code.
          </ThemedText>
        </>
      )}

      {state === 'completed' && (
        <>
          <StatusBadge theme={theme} $status="completed">
            Device connected successfully
          </StatusBadge>
          <ThemedText style={{ color: theme.colors.muted, textAlign: 'center' }}>
            Your mobile device has been authenticated.
          </ThemedText>
          <Button variant="secondary" onClick={handleGenerate}>
            Generate New Code
          </Button>
        </>
      )}

      {state === 'expired' && (
        <>
          <StatusBadge theme={theme} $status="expired">
            QR code expired
          </StatusBadge>
          <ThemedText style={{ color: theme.colors.muted, textAlign: 'center' }}>
            The QR code has expired. Generate a new one to try again.
          </ThemedText>
          <Button variant="primary" onClick={handleGenerate}>
            Generate New QR Code
          </Button>
        </>
      )}

      {state === 'error' && (
        <>
          <ThemedText style={{ color: theme.colors.danger, textAlign: 'center' }}>
            {errorMessage}
          </ThemedText>
          <Button variant="primary" onClick={handleGenerate}>
            Try Again
          </Button>
        </>
      )}
    </QrContainer>
  );
};

export default QrLoginDisplay;
